import React from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Glyph } from './glyphs';
import { cn } from '../../lib/cn';
import { isNavItemActive } from './navModel';
import { initialsFor } from './UserMenu';
import { useAuth } from '../../../hooks/useAuth';
import { Wordmark } from '../../brand';
import { Sheet } from '../mobile/organisms/Sheet';

/**
 * The ConsoleShell sidebar, below 1024.
 *
 * A 260px column that is always there costs a phone half its width, so the
 * sidebar cannot stay a column. IT IS A BOTTOM SHEET, not a side slide-over:
 * everything else dismissable on this track arrives from the bottom edge, and a
 * panel that flies in from the left is the one surface a thumb cannot reach the
 * far edge of to close.
 *
 * So the `Sheet` organism is MOUNTED here rather than reimplemented, exactly as
 * `desktop/complex/Drawer` does below its own handoff. It brings the focus trap,
 * Escape, the refcounted scroll lock, the scrim, the grabber, and -- the reason
 * this changed at all -- an exit animation. `useFocusTrap` and the hand-written
 * `.ds-navdrawer` panel are both gone; a second implementation of all five in
 * the shells layer is the drift the design system exists to prevent.
 *
 * There is no desktop copy: above 1024 ConsoleShell mounts the real
 * `.dsk-sidebar` instead and this component never runs.
 *
 * Portalled to <body> so no `overflow: hidden` ancestor on a page can clip it,
 * which is why the sheet takes `dsa-drawer-sheet` (`position: fixed`, see
 * `patterns/motion.css`) rather than the absolute default.
 *
 * @param {{
 *   id?: string,
 *   open: boolean,
 *   onClose: () => void,
 *   groups: Array<{ id: string, label?: string, items: Array<object> }>,
 *   badge?: string,
 *   label?: string,
 *   header?: React.ReactNode,
 *   className?: string
 * }} props
 */
export function NavDrawer({
  id = 'shell-nav-drawer',
  open,
  onClose,
  groups = [],
  badge,
  label = 'Console',
  header,
  className,
}) {
  const { pathname } = useLocation();
  const { user, isAuthenticated } = useAuth();

  /* No `if (!open) return null` here: the Sheet holds itself in the DOM for the
     length of its own slide-down, and unmounting it on the closing tick is what
     left this surface with an entry animation and no exit. */
  if (typeof document === 'undefined') return null;

  const titleId = `${id}-title`;

  return createPortal(
    <Sheet
      id={id}
      open={Boolean(open)}
      onClose={onClose}
      /* The brand line is still the accessible name; it just lives in the
         drawer's own head row rather than a `SheetHeader`, because that head
         puts the close button opposite the wordmark and `SheetHeader` renders
         `trailing` before `children`. */
      aria-labelledby={titleId}
      className={cn('dsa-drawer-sheet', 'ds-navdrawer-sheet', className)}
      scrimClassName="dsa-drawer-scrim"
    >
      <div className="ds-navdrawer__head">
        <span className="ds-navdrawer__brand" id={titleId}>
          <Wordmark to={null} />
          {badge && <span className="ds-tag">{badge}</span>}
        </span>
        <button
          type="button"
          className="ds-iconbtn ds-iconbtn--bare"
          onClick={onClose}
          aria-label="Close navigation"
        >
          <Glyph name="x" />
        </button>
      </div>

      {header}

      <nav className="ds-navdrawer__nav" aria-label={label}>
        {groups.map((group) => (
          <div className="ds-navdrawer__group" key={group.id}>
            {group.label && <div className="ds-navdrawer__label">{group.label}</div>}
            {group.items.map((item) => {
              const active = isNavItemActive(item, pathname);
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  className="ds-navdrawer__item"
                  aria-current={active ? 'page' : undefined}
                  onClick={onClose}
                >
                  <span className="ds-navdrawer__icon">
                    <Glyph name={item.glyph} filled={active} />
                  </span>
                  {item.label}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {isAuthenticated && (
        <div className="ds-navdrawer__foot">
          <span className="ds-avatar ds-avatar--sm" aria-hidden="true">
            {initialsFor(user)}
          </span>
          <div className="ds-navdrawer__user">
            <div className="ds-navdrawer__username">
              {user?.full_name || user?.name || 'Your account'}
            </div>
            {user?.email && <div className="ds-navdrawer__usermeta">{user.email}</div>}
          </div>
        </div>
      )}
    </Sheet>,
    document.body
  );
}

export default NavDrawer;
